/* Medicus — Style Finder
 * ---------------------------------------------------------------------------
 * A short quiz that narrows the ten Medicus shapes down to one recommendation
 * plus two alternates. It runs in two modes off this one file:
 *   compact  — embedded in the homepage section
 *   full     — the /pages/style-finder page
 *
 * WHERE THE CONTENT LIVES
 *   The styles themselves (name, summary, sizes, tags, photo, link) come from
 *   the section's blocks and are handed to this file as JSON in the page. Edit
 *   those in the theme editor, not here.
 *   The QUESTIONS and the SCORES below are logic, so they live here. Both are
 *   plain data — the wording can be edited in place without touching anything
 *   else.
 *
 * HOW THE MATCH WORKS
 *   Every answer adds (or subtracts) points from each shape. The highest score
 *   is the recommendation; the next two are shown as alternates. Nothing is
 *   hidden from the visitor either way — the full ten are on the page below.
 *   Every number below is traceable to the published Styles & Sizing Guide, and
 *   NO claim is made that a shape is clinically or universally right for anyone.
 * --------------------------------------------------------------------------- */
(function () {
  'use strict';

  /* ---- Questions ---------------------------------------------------------
     skipIf  — return true to skip the question for that set of answers
     multiple — checkbox-style question with a continue button              */
  var QUESTIONS = [
    {
      id: 'hair',
      legend: 'What does the cap need to cover?',
      help: 'Pick the closest. Every cap is cut to order, so nothing here is final.',
      options: [
        { v: 'short', label: 'Short hair, or very little hair' },
        { v: 'medium', label: 'Medium-length hair' },
        { v: 'long', label: 'Long hair I tie back' },
        { v: 'thick', label: 'Thick, curly or afro hair' },
        { v: 'braids', label: 'Braids, locs or twists' },
        { v: 'turban', label: 'A turban' },
        { v: 'hijab', label: 'A hijab' }
      ]
    },
    {
      id: 'tuck',
      legend: 'Do you want your hair tucked inside the cap?',
      help: 'Not every shape is cut to hold hair inside it, so this one matters.',
      skipIf: function (a) { return a.hair === 'short'; },
      options: [
        { v: 'yes', label: 'Yes, tucked in' },
        { v: 'no', label: 'No, I would rather it sat outside' },
        { v: 'either', label: 'I do not mind' }
      ]
    },
    {
      id: 'sit',
      legend: 'Where should it sit?',
      options: [
        { v: 'above', label: 'Above my ears' },
        { v: 'over', label: 'Over my ears' },
        { v: 'full', label: 'Over my ears and neck, for full coverage' }
      ]
    },
    {
      id: 'extras',
      legend: 'Anything else? Choose any that apply.',
      multiple: true,
      options: [
        { v: 'glasses', label: 'I wear glasses or loupes' },
        { v: 'noties', label: 'I would rather not have ties' },
        { v: 'theatre', label: 'I need theatre-level coverage' },
        { v: 'lights', label: 'Overhead lights bother me' }
      ],
      noneLabel: 'None of these',
      continueLabel: 'Show my styles'
    }
  ];

  /* ---- Scores ------------------------------------------------------------
     Keys are the style keys set on each block in the theme editor.          */
  var SCORES = {
    hair: {
      short:  { bandana: 3, captain: 3, crown: 3, duchess: 1 },
      medium: { duchess: 3, bouffant: 2, pony: 2, crown: 1, bandana: 1 },
      long:   { pony: 4, duchess: 2, bouffant: 2 },
      thick:  { bouffant: 3, braid: 2, duchess: 1 },
      braids: { braid: 4, bouffant: 2 }   /* the Braid Pouch is the shape cut for exactly this */
    },
    tuck: {
      yes:    { duchess: 2, bouffant: 2, braid: 2, pony: 1, bandana: -3, captain: -3, crown: -3 },
      no:     { bandana: 1, captain: 1, crown: 1 },
      either: {}
    },
    sit: {
      above: { captain: 2, crown: 2, bandana: -2, pony: -1, braid: -1, ortho: -2 },
      over:  { bandana: 2, duchess: 2, bouffant: 2, pony: 2, braid: 1, captain: -2, crown: -2, ortho: -1 },
      /* "Full coverage" is a specific request, so the hood leads it outright and
         the three shapes that sit above or only over the ears are pushed down. */
      full:  { ortho: 6, braid: 1, bandana: -2, captain: -3, crown: -3 }
    },
    extras: {
      glasses: { captain: 2, crown: 1 },
      noties:  { crown: 2, duchess: 1, bouffant: 1, captain: -1, bandana: -2, pony: -1, braid: -1 },
      theatre: { ortho: 3 },
      lights:  {}
    }
  };

  /* Answering "a turban" or "a hijab" is not a preference to be scored — those
     two shapes are cut for exactly that, so the quiz answers straight away. */
  var OVERRIDE = { turban: 'turban', hijab: 'hijab' };

  /* Used only to break an exact tie, so the same answers always give the same
     result. Order runs from the most forgiving fit to the most specific. */
  var PRIORITY = ['duchess', 'captain', 'crown', 'bouffant', 'pony', 'braid', 'bandana', 'ortho', 'turban', 'hijab'];

  /* The Flux Peak is an add-on, not a shape. Sizing guide: it fits these six. */
  var FLUX = ['bandana', 'captain', 'crown', 'duchess', 'bouffant', 'pony'];

  /* Notes shown under the result. These exist to be straight with people when
     their answers pull against each other, rather than quietly returning a
     shape that cannot do what they asked for. */
  var NOTES = [
    {
      when: function (a, lead) {
        return a.tuck === 'yes' && a.sit === 'above' &&
               ['long', 'thick', 'braids', 'medium'].indexOf(a.hair) > -1;
      },
      text: 'One thing worth saying: none of the shapes that sit above the ears are cut to hold hair inside them. This match sits over the ears instead, because that is what holds hair.'
    },
    {
      when: function (a, lead) { return lead === 'bouffant' && ['thick', 'braids'].indexOf(a.hair) > -1; },
      text: 'The Bouffant 2XL is the one cut specifically for bulky hair, afro hair, braids and locs — worth choosing over the M/L or XL.'
    },
    {
      when: function (a, lead) {
        return a.sit === 'full' && ['long', 'thick', 'braids'].indexOf(a.hair) > -1;
      },
      text: 'The hood is cut for coverage rather than for holding a lot of hair. If holding your hair matters more than covering your neck, look at the alternate below it.'
    },
    {
      when: function (a, lead) { return a.hair === 'short' && a.sit === 'full'; },
      text: 'Full coverage means the hood, which is cut for theatre rather than for hair. If you only wanted your ears covered, the Bandana is the simpler answer.'
    },
    {
      when: function (a, lead) {
        return Array.isArray(a.extras) && a.extras.indexOf('lights') > -1;
      },
      text: 'Add a Flux Peak when you order to keep overhead light out of your eyes. It fits the Bandana, Captain, Crown, Duchess, Bouffant and Pony.'
    }
  ];

  /* ---- Scoring -----------------------------------------------------------
     Module scope on purpose: the product-page fit check runs the SAME ranking
     through MCStyleFinderLogic below, so the two can never drift apart. */
  function rankAnswers(answers, keys) {
    var totals = {};
    keys.forEach(function (k) { totals[k] = 0; });

    Object.keys(answers).forEach(function (qid) {
      var table = SCORES[qid];
      if (!table) return;
      var val = answers[qid];
      var list = Array.isArray(val) ? val : [val];
      list.forEach(function (v) {
        var row = table[v];
        if (!row) return;
        Object.keys(row).forEach(function (k) {
          if (k in totals) totals[k] += row[k];
        });
      });
    });

    var ranked = Object.keys(totals)
      .filter(function (k) { return totals[k] > 0; })
      .sort(function (a, b) {
        if (totals[b] !== totals[a]) return totals[b] - totals[a];
        return PRIORITY.indexOf(a) - PRIORITY.indexOf(b);
      });

    /* A shape nobody scored on still deserves a sensible fallback rather than
       an empty result, so fall back to the priority order. */
    if (!ranked.length) ranked = PRIORITY.filter(function (k) { return keys.indexOf(k) > -1; });
    return ranked;
  }

  /* ---- Helpers ----------------------------------------------------------- */
  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function publish(name, payload) {
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish(name, payload);
      }
    } catch (e) { /* tracking must never break the page */ }
  }

  /* ---- One instance ------------------------------------------------------ */
  function StyleFinder(root) {
    var dataNode = root.querySelector('[data-mc-sf-data]');
    if (!dataNode) return;

    var styles;
    try { styles = JSON.parse(dataNode.textContent).styles || []; } catch (e) { return; }
    if (!styles.length) return;

    var byKey = {};
    styles.forEach(function (s) { if (s.key) byKey[s.key] = s; });

    var stage = root.querySelector('[data-mc-sf-stage]');
    if (!stage) return;

    var location = root.getAttribute('data-mc-sf-location') || 'page';
    var answers = {};
    var path = [];      /* indexes of the questions already answered, in order */

    /* Skip any question whose skipIf says so, and any whose options are all
       irrelevant. Returns -1 when there is nothing left to ask. */
    function nextIndex(from) {
      for (var i = from; i < QUESTIONS.length; i++) {
        var q = QUESTIONS[i];
        if (typeof q.skipIf === 'function' && q.skipIf(answers)) continue;
        return i;
      }
      return -1;
    }

    function totalSteps() {
      var n = 0;
      for (var i = 0; i < QUESTIONS.length; i++) {
        var q = QUESTIONS[i];
        if (typeof q.skipIf === 'function' && q.skipIf(answers)) continue;
        n++;
      }
      return n;
    }

    /* ---- Scoring ---- */
    function score() {
      return rankAnswers(answers, styles.map(function (s) { return s.key; }));
    }

    /* ---- Render: a question ---- */
    function renderQuestion(index) {
      var q = QUESTIONS[index];
      stage.innerHTML = '';

      var asked = path.indexOf(index);
      var shown = (asked === -1 ? path.length : asked) + 1;

      var head = el('div', 'mc-sf__qhead');
      var count = el('p', 'mc-sf__count', 'Question ' + shown + ' of ' + totalSteps());
      head.appendChild(count);

      var bar = el('div', 'mc-sf__bar');
      bar.setAttribute('aria-hidden', 'true');
      var fill = el('span', 'mc-sf__bar-fill');
      fill.style.width = Math.round((shown - 1) / totalSteps() * 100) + '%';
      bar.appendChild(fill);
      head.appendChild(bar);
      stage.appendChild(head);

      var group = el('div', 'mc-sf__q');
      group.setAttribute('role', 'group');
      var legendId = root.id + '-q' + index;
      var legend = el('h3', 'mc-sf__legend', q.legend);
      legend.id = legendId;
      legend.setAttribute('tabindex', '-1');
      group.setAttribute('aria-labelledby', legendId);
      group.appendChild(legend);
      if (q.help) group.appendChild(el('p', 'mc-sf__help', q.help));

      var list = el('div', 'mc-sf__options');
      var chosen = q.multiple ? [] : null;

      q.options.forEach(function (opt) {
        var b = el('button', 'mc-sf__opt');
        b.type = 'button';
        b.appendChild(el('span', 'mc-sf__opt-label', opt.label));
        if (q.multiple) {
          b.setAttribute('aria-pressed', 'false');
          b.addEventListener('click', function () {
            var on = b.getAttribute('aria-pressed') === 'true';
            b.setAttribute('aria-pressed', on ? 'false' : 'true');
            b.classList.toggle('is-on', !on);
            var at = chosen.indexOf(opt.v);
            if (on && at > -1) chosen.splice(at, 1);
            if (!on && at === -1) chosen.push(opt.v);
          });
        } else {
          b.addEventListener('click', function () { answer(index, opt.v); });
        }
        list.appendChild(b);
      });
      group.appendChild(list);
      stage.appendChild(group);

      var foot = el('div', 'mc-sf__foot');
      if (q.multiple) {
        var go = el('button', 'mc-cta mc-cta--primary mc-sf__go', q.continueLabel || 'Continue');
        go.type = 'button';
        go.addEventListener('click', function () { answer(index, chosen.slice()); });
        foot.appendChild(go);
        if (q.noneLabel) {
          var none = el('button', 'mc-cta mc-cta--ghost mc-sf__none', q.noneLabel);
          none.type = 'button';
          none.addEventListener('click', function () { answer(index, []); });
          foot.appendChild(none);
        }
      }
      if (path.length) {
        var back = el('button', 'mc-sf__back');
        back.type = 'button';
        back.innerHTML = '<span aria-hidden="true">←</span> Back';
        back.addEventListener('click', goBack);
        foot.appendChild(back);
      }
      if (foot.childNodes.length) stage.appendChild(foot);

      legend.focus({ preventScroll: true });
    }

    function answer(index, value) {
      var q = QUESTIONS[index];
      answers[q.id] = value;
      if (path.indexOf(index) === -1) path.push(index);

      publish('style_finder_answer', {
        location: location,
        question: q.id,
        answer: Array.isArray(value) ? value.join(',') : value
      });

      if (q.id === 'hair' && OVERRIDE[value]) return renderResult([OVERRIDE[value]], true);

      var next = nextIndex(index + 1);
      if (next === -1) return renderResult(score(), false);
      renderQuestion(next);
    }

    function goBack() {
      if (!path.length) return;
      var prev = path.pop();           /* the question answered just before this one */
      delete answers[QUESTIONS[prev].id];
      renderQuestion(prev);
    }

    /* ---- Render: the result ---- */
    function styleCard(key, lead) {
      var s = byKey[key];
      if (!s) return null;

      var card = el(s.url ? 'a' : 'div', 'mc-sf__rescard' + (lead ? ' mc-sf__rescard--lead' : ''));
      if (s.url) {
        card.href = s.url;
        card.setAttribute('data-mc-event', 'style_finder_shop');
        card.setAttribute('data-mc-label', s.name);
      }

      var media = el('span', 'mc-sf__resmedia');
      if (s.image) {
        var img = new Image();
        img.src = s.image;
        img.alt = s.imageAlt || (s.name + ' scrub cap');
        img.loading = 'lazy';
        img.decoding = 'async';
        img.className = 'mc-sf__resimg';
        media.appendChild(img);
      }
      card.appendChild(media);

      var body = el('span', 'mc-sf__resbody');
      if (lead) body.appendChild(el('span', 'mc-flag', 'Closest match'));
      body.appendChild(el('span', 'mc-sf__resname', s.name));
      if (s.summary) body.appendChild(el('span', 'mc-sf__ressum', s.summary));

      if (lead && s.tags) {
        var tagWrap = el('span', 'mc-sf__tags');
        s.tags.split(',').forEach(function (t) {
          t = t.trim();
          if (t) tagWrap.appendChild(el('span', 'mc-sf__tag', t));
        });
        if (tagWrap.childNodes.length) body.appendChild(tagWrap);
      }
      if (s.sizes) body.appendChild(el('span', 'mc-sf__ressizes', 'Sizes: ' + s.sizes));
      if (s.url) body.appendChild(el('span', 'mc-sf__rescue', 'See the ' + s.name + ' →'));
      card.appendChild(body);
      return card;
    }

    function renderResult(ranked, direct) {
      var lead = ranked[0];
      var alts = ranked.slice(1, 3);

      stage.innerHTML = '';
      var wrap = el('div', 'mc-sf__result');
      wrap.setAttribute('role', 'status');

      wrap.appendChild(el('h3', 'mc-sf__reshead',
        direct ? 'We cut a shape for exactly that' : 'Your closest match'));
      wrap.appendChild(el('p', 'mc-sf__ressub',
        direct
          ? 'It is made to go over what you already wear, in any of our fabrics.'
          : 'Based on your answers. Every shape is on this page, so have a look at the rest too.'));

      var leadCard = styleCard(lead, true);
      if (leadCard) wrap.appendChild(leadCard);

      if (alts.length) {
        wrap.appendChild(el('p', 'mc-sf__altshead', 'Also worth a look'));
        var altWrap = el('div', 'mc-sf__alts');
        alts.forEach(function (k) {
          var c = styleCard(k, false);
          if (c) altWrap.appendChild(c);
        });
        wrap.appendChild(altWrap);
      }

      var notes = [];
      if (!direct) {
        NOTES.forEach(function (n) {
          try { if (n.when(answers, lead, alts)) notes.push(n.text); } catch (e) { /* a bad rule must not break the result */ }
        });
      }
      if (notes.length) {
        var noteWrap = el('div', 'mc-sf__notes');
        notes.forEach(function (n) { noteWrap.appendChild(el('p', 'mc-sf__note', n)); });
        wrap.appendChild(noteWrap);
      }

      var again = el('button', 'mc-sf__again');
      again.type = 'button';
      again.innerHTML = '<span aria-hidden="true">↺</span> Start again';
      again.addEventListener('click', reset);
      wrap.appendChild(again);

      stage.appendChild(wrap);

      publish('style_finder_result', {
        location: location,
        best: lead,
        alternates: alts.join(','),
        hair: answers.hair || '',
        tuck: answers.tuck || '',
        sit: answers.sit || '',
        extras: Array.isArray(answers.extras) ? answers.extras.join(',') : ''
      });

      var h = wrap.querySelector('.mc-sf__reshead');
      if (h) { h.setAttribute('tabindex', '-1'); h.focus({ preventScroll: true }); }
    }

    function reset() {
      answers = {};
      path = [];
      renderQuestion(0);
    }

    root.classList.add('mc-sf--ready');
    var fallback = root.querySelector('[data-mc-sf-nojs]');
    if (fallback) fallback.hidden = true;
    renderQuestion(0);
  }

  function init() {
    var roots = document.querySelectorAll('[data-mc-sf]');
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].__mcSF) continue;
      roots[i].__mcSF = true;
      try { StyleFinder(roots[i]); } catch (e) { /* leave the no-JS content in place */ }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);

  /* ---- Shared with the product-page fit check ----------------------------
     assets/mc-fit-check.js reads its questions, scores and notes from here so
     that the quiz on a product page and the quiz on the homepage can never
     give a different answer to the same four questions. Read-only: treat the
     tables above as the single source of truth and edit them there. */
  window.MCStyleFinderLogic = {
    QUESTIONS: QUESTIONS,
    SCORES: SCORES,
    OVERRIDE: OVERRIDE,
    PRIORITY: PRIORITY,
    FLUX: FLUX,
    NOTES: NOTES,
    rank: rankAnswers
  };
})();
